import { ReactNode, useId } from 'react';
import { makeStyles } from '@material-ui/core';
import { Link, LinkButton, LinkButtonProps } from '@backstage/core-components';

/**
 * Links that leave the portal (SR-01): the target has already been through
 * `toSafeHref`, and the core-components link opens it in a new tab. The
 * accessible name is the pt-BR text given here, ending with "(abre em nova
 * aba)"; it is referenced with `aria-labelledby`, which takes precedence
 * over the English hint that core-components adds to external links.
 */

const useStyles = makeStyles({
  hidden: { display: 'none' },
});

export function ExternalLink(
  props: Readonly<{
    href: string;
    accessibleName: string;
    className?: string;
    title?: string;
    children: ReactNode;
  }>,
) {
  const { href, accessibleName, className, title, children } = props;
  const classes = useStyles();
  const id = useId();
  return (
    <>
      <Link
        to={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-labelledby={id}
        className={className}
        title={title}
        underline="none"
      >
        {children}
      </Link>
      <span id={id} className={classes.hidden}>
        {accessibleName}
      </span>
    </>
  );
}

export function ExternalLinkButton(
  props: Readonly<
    Omit<LinkButtonProps, 'to'> & { href: string; accessibleName: string }
  >,
) {
  const { href, accessibleName, ...rest } = props;
  const classes = useStyles();
  const id = useId();
  return (
    <>
      <LinkButton
        {...rest}
        to={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-labelledby={id}
      />
      <span id={id} className={classes.hidden}>
        {accessibleName}
      </span>
    </>
  );
}
